import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import {
  IAccount,
  IFacility,
  IFacilityGroup,
  IMeter,
  IPage,
} from 'common/src/types/interfaces';

import {
  getFacilities,
  updateFacilities,
} from 'portal/src/api/FacilitiesService';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { isActiveAccount, isActiveMeter } from 'portal/src/utils/checkActive';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import type { DeactivateConfig } from '../common/CustomToolbar';
import DataTable from '../common/DataTable';
import addBooleanColumnProps from '../common/tables/addBooleanColumnProps';
import addDateColumnProps from '../common/tables/addDateColumnProps';
import addNumberColumnProps from '../common/tables/addNumberColumnProps';

const Facilities = () => {
  const navigate = useNavigate();

  const [showDeactivatedFacilities, setShowDeactivatedFacilities] =
    useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    number: false,
  });

  const {
    data: facilities,
    isFetched,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['facilities'],
    queryFn: async () => await getFacilities(),
    initialData: { data: [] } as IPage<IFacility>,
  });

  const gridColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'address1',
      headerName: 'Address',
      minWidth: 160,
      flex: 1,
    },
    { field: 'type', headerName: 'Type', width: 120 },
    {
      field: 'number',
      headerName: 'Number',
      width: 100,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'facilityGroups',
      headerName: 'Group',
      width: 120,
      valueGetter: (value: Partial<IFacilityGroup>[]) =>
        value?.map((facilityGroup) => facilityGroup.name).join(', ') || '',
    },
    {
      field: 'dateClosed',
      ...addDateColumnProps('Date Closed', 100),
    },
    {
      field: 'totalOccupants',
      valueFormatter: (value) => numberFormatter(value, { useGrouping: true }),
      ...addNumberColumnProps('Number Of Occupants'),
    },
    {
      field: 'yearBuilt',
      valueFormatter: (value) => numberFormatter(value, { useGrouping: false }),
      ...addNumberColumnProps('Year Built', false, 80),
    },
    {
      field: 'squareFootage',
      valueFormatter: (value) =>
        numberFormatter(value, { style: 'decimal', maximumFractionDigits: 0 }),
      ...addNumberColumnProps('Square Footage'),
    },
    {
      field: 'accounts',
      valueGetter: (_, { meters = [] } = {}) => {
        const accounts = new Map();
        meters.forEach((m: IMeter) =>
          accounts.set(m.parentAccount.id, m.parentAccount),
        );
        return Array.from(accounts.values()).filter((a: IAccount) =>
          isActiveAccount(a),
        ).length;
      },
      ...addNumberColumnProps('Active Accounts', true),
    },
    {
      field: 'meters',
      valueGetter: (meters: Partial<IMeter>[]) =>
        (meters || []).filter((m: Partial<IMeter>) => isActiveMeter(m)).length,
      ...addNumberColumnProps('Active Meters', true),
    },
    {
      field: 'esPropertyId',
      headerName: 'In Energy Star',
      valueGetter: (esId) => !!esId, // This is needed for filtering to work.
      ...addBooleanColumnProps(),
    },
    {
      field: 'isActive',
      headerName: 'Deactivated',
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: false, yesIsGreen: false },
        includeTooltip: true,
      }),
    },
  ];

  const handleNavigate = (id: string) => navigate(`./${id}`);

  const toolbarDeactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedFacilities,
    onToggleCheckbox: setShowDeactivatedFacilities,
  };

  return (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        changeActiveConfig: {
          targetStr: 'facility',
          onChangeActiveStatus: async (id, isActive) => {
            await updateFacilities(id, { isActive: !isActive });
            await refetch();
          },
          snackbarMsgKey: 'FACILITY',
        },
      }}
      columns={gridColumns}
      columnVisibilityModel={columnVisibilityModel}
      contextName={NESTED_ROUTES.ADMINISTRATION.FACILITIES}
      dataFetchError={isError}
      loading={!isFetched}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      rows={
        showDeactivatedFacilities
          ? facilities.data
          : facilities.data.filter(({ isActive }) => isActive)
      }
      toolbarSlotProps={{
        deactivateConfig: toolbarDeactivateConfig,
        exportFileName: 'facilities',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showColumns: true,
        showFilters: true,
      }}
    />
  );
};

export default Facilities;
